import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PageHeader from '../components/PageHeader';
import DeleteIcon from '@mui/icons-material/Delete';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Chip from '@mui/material/Chip';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as SidebarLineNotifIcon } from '../assets/icons/sidebar_line_notification.svg';

import { useAuth } from '../auth/AuthContext';
import api from '../services/api';
import { StoreNotificationIdCreate, NotificationSetting } from '../types/index';

// ── スタイル定数 ────────────────────────────────────────

const normalFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
    '& fieldset': { border: 'none' },
    '& input': { color: '#212121', fontSize: '15px' },
    '& input::placeholder': { color: '#BDBDBD', opacity: 1 },
  },
};

const AccountSettings: React.FC = () => {
  const { storeId } = useAuth();

  // 状態管理
  const [notificationSettings, setNotificationSettings] = useState<NotificationSetting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // フィールドへスクロール
  const scrollToField = (fieldName: string): void => {
    const element = document.querySelector(`[name="${fieldName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (element as HTMLElement).focus();
    }
  };

  // 通知設定の読み込み（useCallbackでメモ化）
  const loadNotificationSettings = useCallback(async (): Promise<void> => {
    if (!storeId || !storeId.trim()) return;

    try {
      setLoading(true);
      setError(null);

      const response = await api.storeNotification.getAll(storeId);

      // バックエンドデータをフロントエンド形式に変換
      const settings: NotificationSetting[] = response.items.map((item, index) => ({
        id: item.id,
        tempId: item.id,
        notification_id: item.notification_id,
        label: item.label,
        display_order: item.display_order,
        isNew: false,
        isModified: false,
        // 通知できない宛先（宛先不正で自動隔離）と登録経路を画面で判別できるようにする
        isActive: item.is_active !== false,
        lastError: item.last_error ?? null,
        source: item.source ?? 'manual',
      }));

      // データが空の場合は1行追加
      if (settings.length === 0) {
        settings.push(createNewNotificationSetting(1));
      }

      setNotificationSettings(settings);
    } catch (err: any) {
      setError('通知設定の読み込みに失敗しました');

      // エラー時はデフォルトで1行追加
      setNotificationSettings([createNewNotificationSetting(1)]);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  // 初期データ読み込み
  useEffect(() => {
    if (storeId && storeId.trim()) {
      loadNotificationSettings();
    }
  }, [storeId, loadNotificationSettings]);

  // 新しい通知設定オブジェクトの作成
  const createNewNotificationSetting = (order: number): NotificationSetting => {
    const tempId = `new_${Date.now()}_${Math.random()}`;
    return {
      tempId,
      notification_id: '',
      label: `通知${order}`,
      display_order: order,
      isNew: true,
      isModified: false
    };
  };

  // 新しい通知設定を追加
  const addNotificationSetting = (): void => {
    const newOrder = notificationSettings.length > 0
      ? Math.max(...notificationSettings.map(item => item.display_order)) + 1
      : 1;

    setNotificationSettings([
      ...notificationSettings,
      createNewNotificationSetting(newOrder)
    ]);
  };

  // 通知設定を削除
  const removeNotificationSetting = async (tempId: string): Promise<void> => {
    const setting = notificationSettings.find(item => item.tempId === tempId);

    if (!setting) return;

    try {
      setSaving(true);
      setError(null);

      // バックエンドに存在する場合は削除API呼び出し
      if (setting.id && !setting.isNew) {
        await api.storeNotification.delete(setting.id);
      }

      // フロントエンドの状態から削除し、順序を再計算
      const remainingSettings = notificationSettings.filter(item => item.tempId !== tempId);

      // 残った設定の順序を即座に更新（バックエンドにも反映）
      if (remainingSettings.length > 0) {
        const reorderedSettings = remainingSettings.map((item, index) => ({
          ...item,
          display_order: index + 1,
          label: `通知${index + 1}`
        }));

        // 既存項目の順序をバックエンドに即座に更新
        const updatePromises = reorderedSettings
          .filter(item => !item.isNew && item.id) // 既存項目のみ
          .map(async (item) => {
            const updateData = {
              notification_id: item.notification_id,
              label: item.label,
              display_order: item.display_order
            };
            return await api.storeNotification.update(item.id!, updateData);
          });

        await Promise.all(updatePromises);

        // フロントエンドの状態を更新（isModifiedフラグはリセット）
        setNotificationSettings(
          reorderedSettings.map(item => ({
            ...item,
            isModified: false
          }))
        );
      } else {
        // 全て削除された場合は新しい項目を追加
        setNotificationSettings([createNewNotificationSetting(1)]);
      }

      if (!setting.isNew) {
        setSuccessMessage('通知設定を削除しました');
      }
    } catch (err: any) {
      setError('通知設定の削除に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 通知設定の内容を更新
  const updateNotificationSetting = (tempId: string, field: keyof NotificationSetting, value: string): void => {
    const index = notificationSettings.findIndex(item => item.tempId === tempId);
    setNotificationSettings(
      notificationSettings.map(item =>
        item.tempId === tempId
          ? { ...item, [field]: value, isModified: true }
          : item
      )
    );
    const errorKey = `notification_${index}`;
    if (fieldErrors[errorKey]) {
      setFieldErrors(prev => { const next = { ...prev }; delete next[errorKey]; return next; });
    }
  };

  // バリデーション（フィールドレベル）
  const validateSettings = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    const validSettings = notificationSettings.filter(setting => setting.notification_id.trim());

    if (validSettings.length === 0) {
      errors.notification_0 = '通知IDを入力してください';
      return errors;
    }

    // 重複チェック
    const seen = new Set<string>();
    notificationSettings.forEach((setting, index) => {
      if (setting.notification_id.trim()) {
        if (seen.has(setting.notification_id.trim())) {
          errors[`notification_${index}`] = '通知IDが重複しています';
        }
        seen.add(setting.notification_id.trim());
      }
    });

    return errors;
  };

  // 保存処理
  const handleSave = async (): Promise<void> => {
    if (!storeId || !storeId.trim()) {
      setError('店舗IDが設定されていません');
      return;
    }

    // バリデーション
    const validationErrors = validateSettings();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      scrollToField(Object.keys(validationErrors)[0]);
      return;
    }
    setFieldErrors({});

    try {
      setSaving(true);
      setError(null);

      // 空でない設定のみ対象
      const validSettings = notificationSettings.filter(
        setting => setting.notification_id.trim()
      );

      // 新規作成と更新を分けて処理
      const promises = validSettings.map(async (setting) => {
        if (setting.isNew) {
          // 新規作成
          const createData: StoreNotificationIdCreate = {
            store_id: storeId,
            notification_id: setting.notification_id.trim(),
            label: setting.label.trim(),
            display_order: setting.display_order
          };
          return await api.storeNotification.create(createData);
        } else if (setting.isModified && setting.id) {
          // 更新
          const updateData = {
            notification_id: setting.notification_id.trim(),
            label: setting.label.trim(),
            display_order: setting.display_order
          };
          return await api.storeNotification.update(setting.id, updateData);
        }
        return null;
      });

      await Promise.all(promises);

      // データを再読み込み
      await loadNotificationSettings();

      setSuccessMessage('通知設定を保存しました');
    } catch (err: any) {
      setError('通知設定の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // スナックバーを閉じる
  const handleCloseSnackbar = (): void => {
    setSuccessMessage(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="LINE通知設定"
        icon={<SvgIcon component={SidebarLineNotifIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
      />

      {/* アラート */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* コンテンツ */}
      <Container maxWidth="sm" disableGutters>
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
          <Typography sx={{ fontSize: '13px', color: '#616161', mb: 3, lineHeight: 1.7 }}>
            通知を希望される方のLINEから、店舗公式LINEに<br />
            「通知登録」とメッセージをお送りください。
          </Typography>

          {notificationSettings.map((setting, index) => (
            <Box key={setting.tempId} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '13px', color: '#424242' }}>
                  ID:{String(index + 1).padStart(3, '0')}
                </Typography>
                {/* 登録経路。トークルームの「通知登録」で自動登録された宛先も一覧に出ることを明示する */}
                {!setting.isNew && (
                  <Chip
                    label={setting.source === 'webhook' ? '通知登録' : '画面登録'}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '11px' }}
                  />
                )}
                {/* 宛先不正で通知対象から外れた宛先。ここが見えないと通知が飛ばない理由が分からない */}
                {!setting.isNew && setting.isActive === false && (
                  <Chip
                    label="通知できません（再登録してください）"
                    size="small"
                    color="error"
                    sx={{ height: 20, fontSize: '11px' }}
                  />
                )}
              </Box>
              <TextField
                fullWidth
                placeholder="個人ID"
                name={`notification_${index}`}
                value={setting.notification_id}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateNotificationSetting(setting.tempId, 'notification_id', e.target.value)}
                size="small"
                error={!!fieldErrors[`notification_${index}`]}
                helperText={fieldErrors[`notification_${index}`]}
                sx={normalFieldSx}
                InputProps={notificationSettings.length > 1 ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        color="error"
                        onClick={() => removeNotificationSetting(setting.tempId)}
                        disabled={saving}
                        edge="end"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                } : undefined}
              />
            </Box>
          ))}

          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={addNotificationSetting}
            disabled={saving}
            sx={{
              bgcolor: '#E1F5FE',
              color: '#1976D2',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: 'bold',
              px: 2.5,
              py: 0.5,
              textTransform: 'none',
              '&:hover': { bgcolor: '#B3E5FC' },
            }}
          >
            IDを追加
          </Button>

          {/* 保存ボタン */}
          <Box sx={{ mt: 4, mb: 3 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                bgcolor: '#1565C0',
                borderRadius: '12px',
                height: 52,
                fontSize: '16px',
                fontWeight: 'bold',
                textTransform: 'none',
                '&:hover': { bgcolor: '#0D47A1' },
                '&.Mui-disabled': { bgcolor: '#90A4AE', color: 'white' },
              }}
            >
              {saving ? '保存中...' : '保存'}
            </Button>
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountSettings;
